/**
 * AiModePanel — AI 모드 탭 내용. 질문(q) → POST /api/onto/ai-mode → 근거 인용 답변.
 *   답변(마크다운 경량 렌더·세번 클릭) + 근거 카드(클릭=탭/상세 이동) + 관련 질문 칩.
 *   설계: docs/superpowers/specs/2026-07-10-ai-mode-design.md
 */
import { useEffect, useReducer, useRef } from "react";
import { aiThreadStorageKey, threadReducer, followupBody, INITIAL_THREAD } from "./aiThread";
import ClassificationResultReport from "./ClassificationResultReport";
import ProductIdentityCard from "./ProductIdentityCard";

// 답변 텍스트에서 첫 세번(제안 세번) 추출.
function extractHsk(text) {
  const m = String(text || "").match(/\d{4}\.\d{2}-\d{4}|\d{4}\.\d{2}/);
  return m ? m[0] : "";
}

// 경량 마크다운: **굵게**, 세번코드(4015.90-2000) 하이라이트, 줄바꿈·• 리스트.
function renderAnswer(text, onHsk) {
  const lines = String(text || "").split("\n");
  return lines.map((line, i) => {
    const parts = [];
    let rest = line;
    // **bold**
    const boldSplit = rest.split(/(\*\*[^*]+\*\*)/g);
    boldSplit.forEach((seg, j) => {
      if (/^\*\*[^*]+\*\*$/.test(seg)) {
        parts.push(
          <strong key={`b${i}-${j}`}>{highlightHsk(seg.slice(2, -2), onHsk, `${i}-${j}`)}</strong>,
        );
      } else {
        parts.push(<span key={`s${i}-${j}`}>{highlightHsk(seg, onHsk, `${i}-${j}`)}</span>);
      }
    });
    const isBullet = /^\s*[-•]\s/.test(line);
    return (
      <div
        key={i}
        style={{
          marginBottom: line.trim() ? 6 : 2,
          paddingLeft: isBullet ? 14 : 0,
          lineHeight: 1.75,
          fontSize: 14.5,
        }}
      >
        {parts}
      </div>
    );
  });
}

// 텍스트 내 세번코드를 클릭 가능한 칩으로
function highlightHsk(text, onHsk, keyPrefix) {
  const re = /(\d{4}\.\d{2}-\d{4}|\d{4}\.\d{2}|\d{4}호)/g;
  const out = [];
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const code = m[0];
    out.push(
      <button
        key={`${keyPrefix}-h${k++}`}
        onClick={() => onHsk(code)}
        style={{
          border: "none",
          background: "var(--audit-accent-soft, rgba(37,99,235,.1))",
          color: "var(--audit-accent)",
          borderRadius: 4,
          padding: "0 5px",
          fontFamily: "JetBrains Mono, monospace",
          fontSize: "0.92em",
          fontWeight: 700,
          cursor: "pointer",
        }}
        title="세번 상세 보기"
      >
        {code}
      </button>,
    );
    last = m.index + code.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function AiModePanel({ q, onNav, onReportData }) {
  const [thread, dispatch] = useReducer(threadReducer, INITIAL_THREAD);
  const inputRef = useRef(null);
  const genRef = useRef(0); // 스레드 세대 — q 변경/언마운트 시 증가. in-flight 응답이 리셋된 스레드를 오염시키지 않게 함.

  const storageKey = q ? aiThreadStorageKey(q) : "";
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved?.messages?.length) dispatch({ type: "restore", thread: { ...saved, busy: false, error: null } });
    } catch (_) { /* stale browser state is non-fatal */ }
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || thread.busy || !thread.messages.length) return;
    try { sessionStorage.setItem(storageKey, JSON.stringify(thread)); } catch (_) { /* quota/private mode */ }
  }, [storageKey, thread]);

  // body 를 ai-mode 로 보내고, 세대(gen)가 여전히 유효할 때만 결과를 반영.
  const runAiMode = (body, gen, clientMeta = {}) => {
    fetch("/api/onto/ai-mode", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then((d) => { if (gen === genRef.current) dispatch({ type: "answer", answer: d.answer,
        citations: d.citations, related: d.related_questions, conversationId: d.conversation_id,
        fallback: d.fallback, reverseValidation: d.reverse_validation,
        classifierReverseValidation: d.classifier_reverse_validation,
        productResearch: d.product_research, webSources: d.web_sources,
        productConfirmationRequired: d.product_confirmation_required,
        confirmedProductDescription: clientMeta.confirmedProductDescription || d.confirmed_product_description,
        inputAmbiguity: d.input_ambiguity,
        candidateOptions: d.candidate_options,
        selectedFact: clientMeta.selectedFact,
        selectedCandidateHsk: clientMeta.selectedCandidateHsk,
        requestedModel: d.requested_model_label, actualModel: d.model_label,
        providerFallback: d.provider_fallback, modelSucceeded: d.model_succeeded,
        generationStatus: d.generation_status }); })
      .catch((e) => { if (gen === genRef.current) dispatch({ type: "error", error: e.message }); });
  };

  // 새 최상위 질문(q=sq) → 스레드 리셋 후 초기 답변.
  useEffect(() => {
    if (!q) { dispatch({ type: "reset" }); return undefined; }
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.messages?.length) return undefined;
      }
    } catch (_) { /* fetch normally */ }
    const gen = (genRef.current += 1);
    dispatch({ type: "start", q });
    runAiMode({ q }, gen);
    return () => { genRef.current += 1; }; // 언마운트/재실행 시 세대 무효화 → 늦은 응답 무시
  }, [q, storageKey]);

  // 후속 질문 전송 — 스레드 유지(URL 미변경). 대기 중 새 검색이 오면 세대 불일치로 응답 무시.
  const askFollowup = (text) => {
    const t = String(text || "").trim();
    if (!t || thread.busy) return;
    const body = followupBody(t, thread);
    dispatch({ type: "ask", q: t });
    runAiMode(body, genRef.current);
  };

  // 후보 선택은 단순 LLM 후속질문이 아니다. 사용자가 확인한 상품 사실을
  // 원 질문에 결합해 후보 생성부터 역방향 검증까지 전체 파이프라인을 다시 실행한다.
  const selectCandidate = ({ hsk, title, fact }) => {
    if (thread.busy || !fact) return;
    const gen = (genRef.current += 1);
    const selected = `확인된 상품 사실: ${fact}`;
    dispatch({ type: "start", q: `${q} · ${title || hsk}` });
    runAiMode({ q: `${q}\n${selected}`, selected_candidate_hsk: hsk }, gen,
      { selectedFact: fact, selectedCandidateHsk: hsk });
  };

  const confirmProduct = (description) => {
    const confirmed = String(description || "").trim();
    if (!confirmed || thread.busy) return;
    const gen = (genRef.current += 1);
    dispatch({ type: "start", q });
    runAiMode({ q, confirmed_product_description: confirmed }, gen,
      { confirmedProductDescription: confirmed });
  };

  // 검토보고서 데이터 — 스레드 첫 어시스턴트 답변(초기 분류) 기준.
  useEffect(() => {
    if (!onReportData) return;
    const first = thread.messages.find((m) => m.role === "assistant");
    const hsk = first && !first.fallback ? extractHsk(first.content) : "";
    if (!hsk) { onReportData(null); return; }
    const cites = Array.isArray(first.citations) ? first.citations : [];
    onReportData({
      product_name: q,
      decided_hsk: hsk,
      evidence: cites.filter((c) => c.type === "note").map((c) => ({ label: c.label, text: "" })),
      cases: cites.filter((c) => c.type === "case").map((c) => ({ ref_no: c.label, hsk: c.ref || "" })),
      candidates: cites.filter((c) => c.type === "candidate").map((c) => ({ hsk: c.ref || "", reason: c.label })),
      ai_summary: first.content,
    });
  }, [thread.messages, q, onReportData]);

  // 세번코드 클릭 → 세번 상세로 이동 (onNav 있으면 우선, 없으면 pushState)
  const goHsk = (code) => {
    const d = String(code).replace(/\D/g, "");
    const hsk = d.length >= 10 ? d.slice(0, 10) : null;
    const hs4 = d.slice(0, 4);
    if (onNav && hsk) return onNav(hsk);
    const u = new URL(window.location.href);
    u.searchParams.set("audit", "onto-detail");
    u.searchParams.set("hs", hs4);
    if (hsk) u.searchParams.set("hsk", hsk);
    u.searchParams.delete("chapter");
    u.searchParams.delete("section");
    window.history.pushState({}, "", u.toString());
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const goCitation = (c) => {
    if (!c) return;
    if (c.type === "case") {
      const u = new URL(window.location.href);
      u.searchParams.set("audit", "precedents");
      window.history.pushState({}, "", u.toString());
      window.dispatchEvent(new PopStateEvent("popstate"));
      return;
    }
    if (c.ref) return goHsk(c.ref);
  };

  const lastAssistant = [...thread.messages].reverse().find((m) => m.role === "assistant");
  const rels = lastAssistant && Array.isArray(lastAssistant.related) ? lastAssistant.related : [];

  return (
    <div className="audit-fade-in" style={{ maxWidth: 820 }}>
      {thread.messages.map((m, i) =>
        m.role === "user" ? (
          <div key={i} style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0" }}>
            <div style={{ background: "var(--audit-bg-subtle)", borderRadius: 14, padding: "7px 14px",
              fontSize: 14, fontWeight: 600, maxWidth: "85%" }}>{m.content}</div>
          </div>
        ) : (
          <div key={i} style={{ margin: "6px 0 16px" }}>
            {m.productConfirmationRequired ? (
              <ProductIdentityCard query={q} research={m.productResearch} busy={thread.busy} onConfirm={confirmProduct} />
            ) : (
              <ClassificationResultReport message={m} productName={q} onAsk={askFollowup} onHsk={goHsk} onSelectCandidate={selectCandidate} />
            )}
            <details style={{ margin: "10px 0 4px" }}>
              <summary style={{ cursor: "pointer", color: "var(--audit-text-muted)", fontSize: 12.5 }}>AI 상세 설명 보기</summary>
              <div style={{ color: "var(--audit-text)", paddingTop: 10 }}>{renderAnswer(m.content, goHsk)}</div>
            </details>
            {false && (() => {
              const facts = Array.isArray(m.productResearch?.facts) ? m.productResearch.facts : [];
              const rv = m.reverseValidation || {};
              const sections = [
                ["상품 사실", facts.map((f) => `${f.key || "사실"}: ${f.value ?? "확인"}`)],
                ["필수요건", (rv.satisfied || []).map((x) => x.requirement_id || x.fact_key || "충족")],
                ["배제요건", (rv.contradicted || []).map((x) => x.requirement_id || x.fact_key || "배제")],
                ["경계요건", (rv.sibling_assessments || []).map((x) => x.boundary_id || x.target_hsk || "비교")],
              ];
              if (!sections.some(([, items]) => items.length)) return null;
              return <div style={{ marginTop: 14, border: "1px solid #d9dee7", borderRadius: 8, padding: "18px 20px", background: "#fff", boxShadow: "0 1px 3px rgba(30,45,65,.06)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", borderBottom: "2px solid #374151", paddingBottom: 9, marginBottom: 13 }}><div style={{ fontWeight: 800, fontSize: 15, color: "#202938" }}>품목분류 검토보고서</div><div style={{ fontSize: 11, color: "#6b7280" }}>근거 기반 검토</div></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                {sections.map(([title, items]) => <div key={title} style={{ marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #edf0f4" }}>
                  <div style={{ fontWeight: 750, fontSize: 13, color: "#374151", marginBottom: 5 }}>{title}</div>
                  <div style={{ fontSize: 12.5, color: "#4b5563", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{items.length ? items.join("\n") : "확인된 항목 없음"}</div>
                </div>)}
                </div>
                {Array.isArray(rv.missing_facts) && rv.missing_facts.length > 0 && <div style={{ marginTop: 10, paddingTop: 9, borderTop: "1px solid #d6e1ef" }}>
                  <div style={{ fontWeight: 750, fontSize: 13, color: "#263b59", marginBottom: 5 }}>확인이 필요한 경계 사실</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {rv.missing_facts.map((f, fi) => {
                      const key = f.fact_key || f.key || "article_character";
                      const question = key.includes("material") ? "제품의 외부 표면 재질은 천연가죽·인조가죽·플라스틱·방직용 섬유 중 무엇인가요?" : key.includes("construction") ? "제품은 직물·부직포인가요, 아니면 성형·압출 시트인가요?" : `제품의 ${key} 정보를 알려주세요.`;
                      return <button key={`${key}-${fi}`} onClick={() => askFollowup(question)} style={{ border: "1px solid #9bb7d8", borderRadius: 999, background: "#fff", color: "#2457a6", padding: "6px 10px", fontSize: 11.5, cursor: "pointer" }}>{question}</button>;
                    })}
                  </div>
                </div>}
                {m.productResearch?.candidate_hsk && <div style={{ marginTop: 5, padding: "11px 13px", background: "#f3f4f6", borderLeft: "4px solid #4b5563", fontWeight: 800, fontSize: 14, color: "#202938" }}>우선 검토 세번: {String(m.productResearch.candidate_hsk).replace(/^(\d{4})(\d{2})(\d{4})$/, "$1.$2-$3")}</div>}
              </div>;
            })()}
            {m.fallback && (
              <div style={{ fontSize: 11.5, color: "var(--audit-text-muted)", marginTop: 8 }}>
                · AI 모델 생성에 실패해 확정 표현을 제외한 근거 요약으로 표시 중입니다.
              </div>
            )}
            {m.providerFallback && m.modelSucceeded && (
              <div style={{ fontSize: 11.5, color: "var(--audit-text-muted)", marginTop: 8 }}>
                · 요청 모델 {m.requestedModel || "-"}을 사용할 수 없어 {m.actualModel || "대체 모델"}로 생성했습니다.
              </div>
            )}
            {false && m.productResearch && Array.isArray(m.productResearch.sources)
              && m.productResearch.sources.length > 0 && (
              <div style={{ marginTop: 12, border: "1px solid var(--audit-border)", borderRadius: 10,
                padding: "11px 12px", background: "var(--audit-bg-subtle)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#2457a6" }}>
                    외부 상품 조사 · 출처 확인
                  </span>
                  <span style={{ fontSize: 11, color: "var(--audit-text-muted)" }}>
                    {m.productResearch.match_level || m.productResearch.status}
                  </span>
                </div>
                {m.productResearch.identity && (
                  <div style={{ fontSize: 12.5, fontWeight: 650, marginBottom: 5 }}>
                    {m.productResearch.identity}
                  </div>
                )}
                {m.productResearch.candidate_hsk && (
                  <div style={{ fontSize: 12.5, marginBottom: 6 }}>
                    외부 자료 기반 검토 후보: {highlightHsk(
                      String(m.productResearch.candidate_hsk).replace(
                        /^(\d{4})(\d{2})(\d{4})$/, "$1.$2-$3"), goHsk, `research-${i}`)}
                  </div>
                )}
                {m.productResearch.summary && (
                  <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>{m.productResearch.summary}</div>
                )}
                {m.productResearch.caution && (
                  <div style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 6,
                    color: "var(--audit-text-muted)" }}>{m.productResearch.caution}</div>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 9 }}>
                  {m.productResearch.sources.map((s, j) => (
                    <a key={j} href={s.url} target="_blank" rel="noopener noreferrer"
                      style={{ border: "1px solid var(--audit-border)", borderRadius: 999,
                        padding: "4px 9px", fontSize: 11.5, color: "var(--audit-accent)",
                        background: "var(--audit-bg-elevated, var(--audit-bg))", textDecoration: "none" }}>
                      {s.source_type === "official_ruling" ? "공식 판정" : "제조사 자료"} ↗
                    </a>
                  ))}
                </div>
              </div>
            )}
            {false && m.reverseValidation && (
              <div style={{ marginTop: 12, border: "1px solid var(--audit-border)", borderRadius: 10,
                padding: "10px 12px", background: "var(--audit-bg-subtle)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700,
                    color: m.reverseValidation.status === "CONFIRMED" ? "#16803a" : "#b05a00" }}>
                    역방향 검증 · {m.reverseValidation.display_status || "검토의견"}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--audit-text-muted)" }}>
                    {m.reverseValidation.status}
                  </span>
                </div>
                {m.reverseValidation.review_opinion && (
                  <div style={{ fontSize: 12.5, lineHeight: 1.55 }}>{m.reverseValidation.review_opinion}</div>
                )}
                {m.reverseValidation.next_question && (
                  <button onClick={() => askFollowup(m.reverseValidation.next_question)}
                    style={{ marginTop: 8, border: "1px solid var(--audit-border-strong, #cdd1d8)",
                      background: "var(--audit-bg-elevated, var(--audit-bg))", borderRadius: 999,
                      padding: "5px 11px", fontSize: 12, cursor: "pointer", color: "var(--audit-text)" }}>
                    {m.reverseValidation.next_question}
                  </button>
                )}
              </div>
            )}
            {false && Array.isArray(m.citations) && m.citations.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--audit-border)" }}>
                <div style={{ fontSize: 12, color: "var(--audit-text-muted)", marginBottom: 8 }}>
                  근거 {m.citations.length}건
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {m.citations.map((c, j) => (
                    <button key={j} onClick={() => goCitation(c)}
                      style={{ border: "1px solid var(--audit-border)", background: "var(--audit-bg-elevated, var(--audit-bg))",
                        borderRadius: 8, padding: "6px 12px", fontSize: 12.5,
                        cursor: c.ref || c.type === "case" ? "pointer" : "default",
                        color: "var(--audit-text)", display: "inline-flex", alignItems: "center", gap: 6 }}
                      title={c.ref ? `${c.ref} 상세` : c.label}>
                      <span style={{ color: "var(--audit-text-muted)", fontSize: 11 }}>
                        {c.type === "rate" ? "관세율표" : c.type === "case" ? "분류사례"
                          : c.type === "candidate" ? "후보" : "근거"}
                      </span>
                      {c.label}
                      {c.ref && <span style={{ color: "var(--audit-accent)", fontFamily: "JetBrains Mono, monospace", fontWeight: 700 }}>
                        ({String(c.ref).replace(/\D/g, "").replace(/^(\d{4})(\d{2})(\d{4})$/, "$1.$2-$3")})
                      </span>}
                      {(c.ref || c.type === "case") && <span style={{ color: "var(--audit-accent)" }}>↗</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ),
      )}

      {thread.busy && (
        <div style={{ color: "var(--audit-text-muted)", fontSize: 13.5, padding: "8px 4px" }}>
          AI가 답변을 작성 중입니다…
        </div>
      )}
      {thread.error && (
        <div style={{ color: "#d93025", fontSize: 13, padding: "8px 4px" }}>
          답변을 불러오지 못했습니다 — {thread.error}
        </div>
      )}

      {/* 관련 질문 → 스레드 내 후속 */}
      {!thread.busy && rels.length > 0 && (
        <div style={{ marginTop: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 12, color: "var(--audit-text-muted)", marginBottom: 8 }}>관련 질문</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {rels.map((r, i) => (
              <button key={i} onClick={() => askFollowup(r)}
                style={{ border: "1px solid var(--audit-border-strong, #cdd1d8)", background: "transparent",
                  borderRadius: 999, padding: "6px 14px", fontSize: 13, cursor: "pointer",
                  color: "var(--audit-text)", fontFamily: "inherit" }}>
                {r}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 하단 입력창 — 후속 질문 */}
      <div style={{ position: "sticky", bottom: 0, background: "var(--audit-bg)",
        borderTop: "1px solid var(--audit-border)", paddingTop: 10, marginTop: 14, display: "flex", gap: 8 }}>
        <input
          ref={inputRef}
          placeholder="이 분류에 대해 더 물어보세요"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              askFollowup(e.currentTarget.value);
              e.currentTarget.value = "";
            }
          }}
          disabled={thread.busy}
          style={{ flex: 1, border: "1px solid var(--audit-border-strong, #cdd1d8)", borderRadius: 10, padding: "9px 12px",
            fontSize: 14, background: "var(--audit-bg-elevated, #fff)", color: "var(--audit-text)",
            opacity: thread.busy ? 0.72 : 1, fontFamily: "inherit", outline: "none" }}
        />
        <button
          onClick={() => { if (inputRef.current) { askFollowup(inputRef.current.value); inputRef.current.value = ""; } }}
          disabled={thread.busy}
          style={{ border: "none", background: thread.busy ? "var(--audit-border-strong, #cdd1d8)" : "var(--audit-accent)",
            color: "#fff", borderRadius: 10, padding: "0 16px", fontSize: 14, fontWeight: 600,
            cursor: thread.busy ? "wait" : "pointer", transition: "background .15s ease" }}>
          {thread.busy ? "답변 작성 중…" : "질문 보내기"}
        </button>
      </div>
    </div>
  );
}
